import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import type {
  SolfegeLevel,
  SolfegeQuestionObject,
} from '../uTheory.Skills/solfegeQuestionGenerator';
import SolfegeQuestionGenerator from '../uTheory.Skills/solfegeQuestionGenerator';
import utils from '../utils';
import type KeySignature from '../uTheory.Notation/keySignature';
import type { ModeAlias } from '../uTheory.Notation/scale';
import Scale from '../uTheory.Notation/scale';
import InteractivePrototype from './interactivePrototype';
import * as Skills from '../uTheory.Skills/skills';
import type { OneNoteSolfegeIDOptions } from './oneNoteSolfegeID';
import type { OneNoteSolfegeStaffIDOptions } from './oneNoteSolfegeStaffID';
import Tonicization from '../uTheory.Notation/tonicization';
import type { MultipleNoteSolfegeIDOptions } from './multipleNoteSolfegeID';
import type { MultipleNoteSolfegeStaffIDOptions } from './multipleNoteSolfegeStaffID';
import SolfegeSystem from '../uTheory.Notation/solfegeSystem';
import type { ScaleDegree } from './solfegeDefinitions';

export abstract class SolfegeDictationInteractivePrototype extends InteractivePrototype {
  generator = new SolfegeQuestionGenerator();

  playTonicCount: number;

  tonicizer: Tonicization;

  key: KeySignature;

  system: SolfegeSystemName;

  solfegeSystem: SolfegeSystem;

  declare currentQuestion: SolfegeQuestionObject;

  declare questions: SolfegeQuestionObject[];

  abstract options:
    | OneNoteSolfegeIDOptions
    | OneNoteSolfegeStaffIDOptions
    | MultipleNoteSolfegeIDOptions
    | MultipleNoteSolfegeStaffIDOptions;

  levelData: SolfegeLevel;

  /**
   * Called if options.levelFromUserData is true
   */
  getLevel(userData = this.userData, dictationType: 'single' | 'multiple') {
    return this.generator.getRecommendedLevel(userData, dictationType);
  }

  setLevelFromSkill(options: OneNoteSolfegeIDOptions | OneNoteSolfegeStaffIDOptions) {
    const sqg = this.generator;
    const levels = sqg.levels;
    let skill = utils.isArray(options.skills) ? options.skills[0] : options.skills;
    skill = Skills.makeString(skill);

    if (!skill) {
      throw new Error(
        `${'OneNoteSolfegeID requires a recognized skill ID in string or int id form, not '}${skill}.`
      );
    }

    const level = levels.find((l) => l.singleNoteSkill === skill || l.multiNoteSkill === skill);
    if (typeof level === 'undefined') {
      throw new Error(`${'OneNoteSolfegeID does not train the skill '}${skill}`);
    }

    return level.level;
  }

  setSolfegeSystem() {
    const system =
      this.userData?.preferences?.solfegeMethod || this.options?.system || 'scaleDegrees';

    if (SolfegeSystem.isValidSystemName(system)) {
      this.system = system;
    } else {
      this.system = 'scaleDegrees';
    }
    this.solfegeSystem = new SolfegeSystem(this.system);
  }

  generateQuestions(questions: string[], staff: boolean = false): SolfegeQuestionObject[] {
    if (typeof this.options.level === 'undefined') {
      throw new Error('Undefined Level: In manual question mode, a level must be set.');
    }

    questions = questions || this.options.questions;
    this.questionPrompt = `Identify the ${this.solfegeSystem} you hear.`;

    const generator = this.generator;
    const level = this.options.level;
    this.levelData = generator.levels.find((l) => l.level === level);
    this.questions = this.options.drill
      ? generator.generateQuestions(level, this.options.maxQuestions, this.options, staff)
      : generator.processManualQuestions(questions, level);

    if (!this.optionsTrue('testMode')) {
      this.questionPrompt += `<br /><small>#{question} of ${this.questions.length}`;
    }

    return this.questions;
  }

  /**
   * Delays fading for 2.5 s to allow tonicization to play
   */
  override fadeInstructionsPanel(
    button: HTMLButtonElement,
    div: HTMLDivElement = this.instructionsPanelDiv
  ) {
    // Disable "Start" button
    button.onclick = undefined;
    button.disabled = true;
    button.style.transition = 'opacity 1s';
    button.style.opacity = '0';

    window.setTimeout(() => super.fadeInstructionsPanel(button, div), 2500);
  }

  abstract getSkillFromLevel(options: this['options']): string;

  getLevelFromOptions(options: this['options']): SolfegeLevel {
    const sqg = this.generator;
    const levels = sqg.levels;
    if (utils.isInteger(options.level)) {
      const level = levels.find((l) => l.level === options.level);
      if (typeof level !== 'undefined') {
        return level;
      }
    }
    throw new Error('Solfege ID exercises require an integer from 0 - 10 in options.level');
  }

  protected getSingleNoteSkillFromLevel(options: this['options']): string {
    const level = this.getLevelFromOptions(options);
    return level.singleNoteSkill;
  }

  playTonicization() {
    const tempo = 90;
    if (!this.tonicizer) {
      this.tonicizer = new Tonicization(this.player);
    }
    this.tonicizer.tonicize(this.key, tempo).then(() => {
      this.play(0, true);
    });
  }

  abstract play(startSecond: number, doNotDecrement: boolean);

  abstract playHint(scaleDegree?: string);

  playAnswer() {} // just here to override

  playTonic() {
    if (this.options.testMode) {
      if (!this.playTonicCount) return;
      this.playTonicCount = this.decrementPlayCount(
        this.letterPanel!.namedCustomButtons.playTonic!,
        this.playTonicCount
      );
    }
    this.player.play(this.key.tonic);
  }

  /**
   * Takes a button element & a count integer, updates the
   * button to read something like "Play Tonic (2)", returns
   * the decremented integer.
   */
  decrementPlayCount(button: HTMLButtonElement, count: number) {
    if (!count) return -1;

    button.innerHTML = `${button.innerHTML.slice(0, -3)}(${--count})`;

    // Update button text / disable button if needed
    if (count === 0) {
      button.disabled = true;
    }

    return count;
  }
}

export abstract class MultipleNoteSolfegeDictationInteractivePrototype extends SolfegeDictationInteractivePrototype {
  playNotesCount: number;

  questionPartCount: number = 0;

  gameStarted: boolean = false;

  answerStack: {
    answer: ScaleDegree;
    correct?: boolean;
    correctAnswer?: ScaleDegree;
  }[];

  displayInstructions(disableAudio = false) {
    const firstQuestion = this.questionNumber === 0 && this.questionPartCount === 0;
    const differentKey =
      !!this.key && !!this.currentQuestion.key && !this.key.equals(this.currentQuestion.key);
    if (firstQuestion || differentKey) {
      this.key = this.currentQuestion.key;
    }

    super.displayInstructions();
    if (disableAudio) return;
    this.player.stop();

    if (firstQuestion || differentKey) {
      this.playTonicization();
    } else {
      this.play();
    }
  }

  play(startSecond?: number, doNotDecrement?: boolean) {
    if (this.options.testMode && !doNotDecrement) {
      this.playNotesCount = this.decrementPlayCount(
        this.letterPanel!.namedCustomButtons.playNotes!,
        this.playNotesCount
      );
      if (!this.playNotesCount) return;
    }

    startSecond = startSecond || 0;
    const tempo = 90;
    const beatDuration = 60 / tempo;

    const { notes } = this.currentQuestion;
    notes.forEach((note, index) => {
      const startTime = startSecond + index * beatDuration;
      let stopTime = startTime + beatDuration + 0.4;
      if (index === notes.length - 1) stopTime = undefined;
      this.player.play(note, { startTime, stopTime });
    }, this);
  }

  abstract getScaleAndFirstWrongDegree(): { degree: string | false; scale?: Scale };

  playHint() {
    const { scale, degree } = this.getScaleAndFirstWrongDegree();
    // There may be no wrong answer that's not an octave issue:
    if (!degree || !scale) return;

    const { tonicTriad } = this.key;
    tonicTriad.setOctave(scale.tonic.octave);

    this.player.play(tonicTriad, { velocity: 0.5 });
    this.player.play(scale[1]);
    this.player.play(scale[degree], { startTime: 0.5 });
    for (let i = 1; i <= Number(degree); i++) {
      this.player.play(scale[i], {
        startTime: 1 + 0.35 * i,
        stopTime: i === Number(degree) ? undefined : 1.4 + 0.35 * i,
        velocity: 0.75,
      });
    }
  }

  getSkillFromLevel(options: this['options']) {
    return this.getLevelFromOptions(options).multiNoteSkill;
  }

  get firstNoteGivenOffset(): 0 | 1 {
    return this.levelData.firstNoteGiven ? 1 : 0;
  }

  override newQuestion(disableAudio = false) {
    disableAudio = disableAudio || !this.gameStarted;
    if (!this.questionPartCount) this.answerStack = [];
    this.wrongAnswerCount = 0;

    if (this.options.testMode) {
      const repetition =
        typeof this.options.repetition !== 'undefined' ? this.options.repetition : 3;
      this.playTonicCount = repetition;
      this.playNotesCount = repetition;
      this.letterPanel!.namedCustomButtons.playTonic!.innerHTML = `Play T<u>o</u>nic (${this.playTonicCount})`;
      this.letterPanel!.namedCustomButtons.playTonic!.disabled = false;
      this.letterPanel!.namedCustomButtons.playNotes!.innerHTML = `Play <u>N</u>ote (${this.playNotesCount})`;
      this.letterPanel!.namedCustomButtons.playNotes!.disabled = false;
    }

    super.newQuestion(disableAudio);
  }

  override wrongAnswer(warningText?) {
    if (++this.wrongAnswerCount > 1) {
      this.playHint();
    }
    super.wrongAnswer(warningText);
  }
}

export abstract class OneNoteSolfegeDictationInteractivePrototype extends SolfegeDictationInteractivePrototype {
  gameStarted: boolean = false;

  playNoteCount: number;

  declare options: OneNoteSolfegeIDOptions | OneNoteSolfegeStaffIDOptions;

  override newQuestion(disableAudio: any) {
    disableAudio = disableAudio || !this.gameStarted;
    if (this.currentQuestion) this.player.noteOff(this.currentQuestion.note);
    this.wrongAnswerCount = 0;
    if (this.options.testMode) {
      const repetition =
        typeof this.options.repetition !== 'undefined' ? this.options.repetition : 3;
      this.playTonicCount = repetition;
      this.playNoteCount = repetition;
      // TODO: Refactor letterPanel so that you can't set a prop name on customButtons array
      this.letterPanel!.namedCustomButtons.playTonic!.innerHTML = `Play T<u>o</u>nic (${this.playTonicCount})`;
      this.letterPanel!.namedCustomButtons.playTonic!.disabled = false;
      this.letterPanel!.namedCustomButtons.playNote!.innerHTML = `Play <u>N</u>ote (${this.playNoteCount})`;
      this.letterPanel!.namedCustomButtons.playNote!.disabled = false;
    }

    super.newQuestion(disableAudio);
  }

  override displayInstructions(disableAudio?: any) {
    super.displayInstructions();
    if (disableAudio) return;

    this.player.stop();
    const firstQuestion = !this.key;
    const differentKey =
      !!this.key && !!this.currentQuestion.key && !this.key.equals(this.currentQuestion.key);

    if (firstQuestion || differentKey) {
      this.key = this.currentQuestion.key;
      this.playTonicization();
    } else {
      this.player.play(this.currentQuestion.note);
    }
  }

  getSkillFromLevel(options: this['options']) {
    return this.getSingleNoteSkillFromLevel(options);
  }

  wrongAnswer(
    text?: string,
    options: { noPlay?: boolean; skills?: (string | number)[]; noProgressTracker?: boolean } = {}
  ) {
    if (++this.wrongAnswerCount > 1) {
      this.playHint();
    }

    super.wrongAnswer(text, options);
  }

  play(startSecond: number, doNotDecrement: boolean) {
    if (this.options.testMode && !doNotDecrement) {
      if (!this.playNoteCount) return;
      this.playNoteCount = this.decrementPlayCount(
        this.letterPanel!.namedCustomButtons.playNote!,
        this.playNoteCount
      );
    }

    this.player.play(this.currentQuestion.note, { startTime: startSecond });
  }

  playHint() {
    let quality: string = this.key.quality;
    let answer = this.currentQuestion.degree;
    if (answer === 'M6') {
      quality = 'melodic minor';
      answer = '6';
    }
    if (answer === 'M7') {
      quality = 'harmonic minor';
      answer = '7';
    }
    const scale = new Scale(this.key.tonic, quality as ModeAlias);

    const tonicTriad = this.key.tonicTriad;
    tonicTriad.setOctave(tonicTriad.octave - 1);

    this.player.play(tonicTriad, { velocity: 0.5 });
    this.player.play(scale[1]);
    this.player.play(scale[answer], { startTime: 0.5 });
    for (let i = 1; i <= Number(answer); i++) {
      this.player.play(scale[i], {
        startTime: 1 + 0.35 * i,
        stopTime: i === Number(answer) ? undefined : 1.4 + 0.35 * i,
        velocity: 0.75,
      });
    }
  }
}
